import { memo, useRef, type CSSProperties } from "react";
import { Link } from "@tanstack/react-router";
import {
  MoreHorizontalIcon,
  PlusIcon,
  CircleStopIcon,
  BookOpenIcon,
  FilesIcon,
} from "lucide-react";
import type { Framework } from "../../state/governance";
import { Button } from "../ui/button";
import { Card } from "../ui/card";
import { CardSpotlight } from "../ui/card-spotlight";
import { Menu, MenuItem, MenuPopup, MenuSeparator, MenuTrigger } from "../ui/menu";
import { regimeCardColors } from "./regime-artwork";
import { RegimeArtwork } from "./RegimeArtwork";
import { RegimeAddButton } from "./RegimeAddButton";

interface Props {
  framework: Framework;
  orgSegment: string;
  canManage: boolean;
  busy: boolean;
  error: string | null;
  onChange: (id: string, enabled: boolean, anchor?: HTMLButtonElement) => Promise<void>;
}

/** "Jersey · JFSC" when the issuer does not name the place; the issuer alone when it does. */
function frameworkMeta(framework: Framework): string {
  const issuer = framework.issuer ?? "";
  const jurisdiction = framework.jurisdiction ?? "";
  if (issuer.length === 0) return jurisdiction;
  if (jurisdiction.length === 0) return issuer;
  const named = jurisdiction
    .split(/ — | \/ /)
    .some((part) => part.length >= 3 && issuer.toLowerCase().includes(part.toLowerCase()));
  return named ? issuer : `${jurisdiction} · ${issuer}`;
}

/** Only changed cards update when the catalogue snapshot is revalidated. */
export const RegimeCard = memo(
  function RegimeCard({ framework, orgSegment, canManage, busy, error, onChange }: Props) {
    const menuButton = useRef<HTMLButtonElement>(null);
    return (
      <Card
        render={<li />}
        className="regime-card isolate min-w-0 cursor-pointer gap-3 border-border/60 p-4"
        style={regimeCardColors(framework.id) as CSSProperties}
      >
        <CardSpotlight />
        <div className="flex items-start gap-3">
          <RegimeArtwork regimeId={framework.id} />
          <div className="min-w-0 flex-1">
            <Link
              to="/$org/compliance/regimes/$regimeId"
              params={{ org: orgSegment, regimeId: framework.id }}
              className="line-clamp-3 text-sm font-medium leading-snug text-foreground after:absolute after:inset-0 hover:underline focus-visible:outline-none"
              title={framework.name}
            >
              {framework.name}
            </Link>
          </div>
        </div>
        <div>
          {/* Keep issuer and edition aligned with the card, below the title row. */}
          <p className="line-clamp-3 text-xs leading-relaxed text-muted-foreground">
            {[frameworkMeta(framework), framework.description?.trim()].filter(Boolean).join(" · ")}
          </p>
          {framework.version ? (
            <p
              className="mt-0.5 truncate text-xs text-muted-foreground/80"
              title={framework.version}
            >
              {framework.version}
            </p>
          ) : null}
        </div>

        <div className="mt-auto space-y-3">
          {framework.enabled && framework.controlsSatisfied > 0 ? (
            <div className="h-1.5 overflow-hidden rounded-full bg-muted">
              <div
                className="h-full rounded-full bg-primary transition-[width] duration-500 motion-reduce:transition-none"
                style={{ width: `${framework.percentComplete ?? 0}%` }}
              />
            </div>
          ) : null}
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 text-xs text-muted-foreground">
            <Link
              to="/$org/compliance/regimes/$regimeId"
              params={{ org: orgSegment, regimeId: framework.id }}
              hash="publications"
              className="relative z-10 underline-offset-4 hover:text-foreground hover:underline"
            >
              Sources & scraping
            </Link>
            <span className="ml-auto text-right tabular-nums">
              {framework.controlTotal === 0
                ? "No controls loaded"
                : framework.enabled
                  ? `${framework.controlsSatisfied} of ${framework.controlTotal} addressed`
                  : `${framework.controlTotal} headline controls`}
            </span>
          </div>
        </div>

        {error ? (
          <p role="alert" className="relative z-10 text-xs text-destructive">
            {error}
          </p>
        ) : null}
        <div className="flex items-center gap-2">
          <RegimeAddButton
            added={framework.enabled}
            busy={busy}
            disabled={!canManage}
            onAdd={(button) => void onChange(framework.id, true, button)}
            onRemove={(button) => void onChange(framework.id, false, button)}
          />
          <Menu>
            <MenuTrigger
              render={
                <Button
                  ref={menuButton}
                  variant="ghost"
                  size="icon-sm"
                  aria-label={`Options for ${framework.name}`}
                  className="relative z-10 cursor-pointer text-muted-foreground"
                />
              }
            >
              <MoreHorizontalIcon className="size-3" />
            </MenuTrigger>
            <MenuPopup align="end">
              <MenuItem
                disabled={!canManage || busy}
                onClick={() =>
                  void onChange(framework.id, !framework.enabled, menuButton.current ?? undefined)
                }
                variant={framework.enabled ? "destructive" : "default"}
                className="cursor-pointer"
              >
                {framework.enabled ? <CircleStopIcon /> : <PlusIcon />}
                {framework.enabled ? "Stop" : "Adopt"}
              </MenuItem>
              <MenuSeparator />
              <MenuItem
                className="cursor-pointer"
                render={
                  <Link
                    to="/$org/compliance/regimes/$regimeId"
                    params={{ org: orgSegment, regimeId: framework.id }}
                  />
                }
              >
                <BookOpenIcon />
                See regime
              </MenuItem>
              <MenuItem
                className="cursor-pointer"
                render={
                  <Link
                    to="/$org/compliance/regimes/$regimeId"
                    params={{ org: orgSegment, regimeId: framework.id }}
                    hash="publications"
                  />
                }
              >
                <FilesIcon />
                See sources
              </MenuItem>
            </MenuPopup>
          </Menu>
        </div>
      </Card>
    );
  },
  (previous, next) =>
    previous.orgSegment === next.orgSegment &&
    previous.canManage === next.canManage &&
    previous.busy === next.busy &&
    previous.error === next.error &&
    previous.onChange === next.onChange &&
    (Object.keys(previous.framework) as Array<keyof Framework>).every(
      (key) => previous.framework[key] === next.framework[key],
    ),
);
